import { makeStyles, Snackbar, withStyles } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { useIO, useLinked, useModel } from "@type-r/react";
import { RowH1 } from "common";
import { CreateWorkflow, div, Flex, Loading, navigate, Page, WorkflowProps } from "lqx-react";
import * as React from "react";
import { useState } from "react";
import { AssetFilterCriteria, WorkSheet } from "../../api/assetFilterCriteria";
import { AssetTypeItem } from "../../api/assetTypes";
import { CompanyItem } from "../../api/company";
import { ImportTemplateDetailsDto } from "../../api/importTemplateDetails";
import { validation } from "../../api/importTemplateValidation";
import { ComputedField, ImportTemplateDetails, Mapping, Metadata, StaticField } from "../models/import-template-details";
import { importTemplate } from "../models/import-wizard-constants";
import { ImportTemplateDetailsStep } from "./import-template-details-step";
import { ImportTemplateMapperStep } from "./import-template-mapper-step";
import { UploadImportTemplateStep } from "./import-template-upload-step";

export const ImportTemplatesWizardPage = ({ id }: { id?: string }) => {
  const defaultImportTemplateDetails = {
    id: '',
    isDefault: false,
    metadata: { dateFormat: importTemplate.defaultDateFormat } as Metadata,
    companies: [] as CompanyItem[],
    mappings: [] as Mapping[],
    staticFields: [] as StaticField[],
    computedFields: [] as ComputedField[],
    isSingleAsset: true,
    assetTypes: [] as AssetTypeItem[],
    name: '',
    file: null,
    originalFileName: null,
    isActive: true,
    isStandard: false,
    omitUnmappedFields: false,
    delimiter: '',
    assetFilterCriteria: {} as AssetFilterCriteria,
    workSheet: { id: 0, name: '' } as WorkSheet,
  }
  const [importTemplateDetails, setImportTemplateDetails]
    = useState(defaultImportTemplateDetails as ImportTemplateDetails);
  const isEdit = id ? true : false;
  const isCreate = !isEdit;

  const $workflowStep = useLinked(0);

  const $doStartOver = useLinked(false);
  const $showErrors = useLinked(false);
  const $errorMessage = useLinked(["Default Error!"]);
  const $showSuccessfulEditMessage = useLinked(false);

  const classes = useStyles();

  const importTemplateDetailsDto = useModel(ImportTemplateDetailsDto);

  const getFileExtension = (name: string) => {
    if (!name) return '';

    let array = name.split('.');
    if (array.length === 1 || (array[0] === "" && array.length === 2)) {
      return '';
    }

    return array.pop();
  }

  const getFileNameWithExtension = (name, extension) => {
    if (!extension) return name;

    return `${name}.${extension}`;
  }

  const getFile = async (url, name) => {
    const response = await fetch(url);
    const blob = await response.blob()

    return new File([blob], name);
  }

  const isEditDataReady = useIO(
    async () => {
      if (!id) return;

      let result: ImportTemplateDetailsDto = null;
      await importTemplateDetailsDto.get(id)
        .then(card => result = card)
        .catch(err => {
          $showErrors.set(true);
          $errorMessage.set(err.message);
        });

      const getMappings = (mappings) => {
        return mappings.map(i => ({
          assetTypeId: i.assetTypeId,
          validationRuleId: i.validationRuleId,
          mappingItems: i.mappingItems.map((mappingItem, index) => ({
            from: {
              name: mappingItem.from.name, x: mappingItem.from.x, y: mappingItem.from.y, isGlobal: mappingItem.from.isGlobal, globalValuePosition: mappingItem.from.globalValuePosition, formatting: mappingItem.from.formatting ?
                { name: mappingItem.from.formatting.name, value: mappingItem.from.formatting.value } : null
            },
            to: { id: mappingItem.to.id.toString(), name: mappingItem.to.name, targetFieldTypeId: mappingItem.to.targetFieldTypeId },
            isChild: mappingItem.isChild ? mappingItem.isChild : false,
            index: index + 1
          }))
        }));
      }

      const getStatics = (staticFields) => {
        return staticFields.map(i => ({
          assetTypeId: i.assetTypeId,
          staticFieldItems: i.staticFieldItems.map(staticItem => ({
            fieldName: staticItem.fieldName, value: staticItem.value, fieldId: staticItem.fieldId
          }))
        }));
      }

      const getComputeds = (computedFields) => {
        return computedFields.map(i => ({
          assetTypeId: i.assetTypeId,
          computedFieldItems: i.computedFieldItems.map(computedItem => ({
            field1: computedItem.field1, field2: computedItem.field2, operator: computedItem.operator, resultField: computedItem.resultField
          }))
        }));
      }

      if (result) {

        const fileName = getFileNameWithExtension(result.name, getFileExtension(result.file))
        const file = await getFile(result.url, fileName);
        const dateFormat = Boolean(result.metadata.dateFormat)
          ? result.metadata.dateFormat
          : importTemplate.defaultDateFormat;

        let details = {
          id: id,
          name: result.name,
          originalFileName: result.originalFileName,
          assetTypes: result.assetTypes.map((item) => item),
          file: result.isStandard ? null : file,
          isDefault: result.isDefault,
          isActive: result.isActive,
          isStandard: result.isStandard,
          metadata: { dateFormat: dateFormat },
          companies: result.companies.map(item => item),
          mappings: getMappings(result.mappings),
          staticFields: getStatics(result.staticFields),
          computedFields: getComputeds(result.computedFields),
          isSingleAsset: true,
          omitUnmappedFields: result.omitUnmappedFields,
          delimiter: result.delimiter,
          assetFilterCriteria: result.assetFilterCriteria,
          workSheet: result.workSheet
        } as ImportTemplateDetails;

        setImportTemplateDetails({
          ...importTemplateDetails,
          ...details
        });
      }
    }
  );

  const doStartOver = () => $doStartOver.set(true);

  const goToStep = (step: number) => $workflowStep.set(step);;

  const goToNextStep = async (inputDetails) => {
    if ($workflowStep.value === 0) {
      const validationResult = await validation(inputDetails.id, inputDetails.name, inputDetails.assetTypes, inputDetails.isDefault, inputDetails.companies);
      if (!validationResult.isValid) {
        $showErrors.set(true);
        $errorMessage.set(validationResult.errors);
        return;
      }
    }
    handleState({
      ...importTemplateDetails,
      ...inputDetails
    });
    $workflowStep.set($workflowStep.value + 1);
  };

  const handleState = (inputDetails) => {
    setImportTemplateDetails({
      ...importTemplateDetails,
      ...inputDetails
    });
  };

  const handleCloseShowErrors = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    $showErrors.set(false);
  };

  const handleCloseShowSuccessfulEditMessage = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    $showSuccessfulEditMessage.set(false);
  };

  const goToPreviousStep = () => $workflowStep.set($workflowStep.value - 1);

  const cancel = () => { navigate('import-templates'); };

  const isStartOverReady = useIO(
    async () => $doStartOver.isTruthy && await startOver(),
    [$doStartOver.value]
  );

  const startOver = async () => {
    try {
      $workflowStep.set(0);
      setImportTemplateDetails(defaultImportTemplateDetails as ImportTemplateDetails);
    }
    finally {
      $doStartOver.set(false);
    }
  };

  const isDetailsStepEnabled = () => $workflowStep.value === 0;

  const isUploadStepEnabled = () => {
    if (isEdit) return false;

    return isCreate && $workflowStep.value === 1;
  }

  const isMapperStepEnabled = () => {
    if (isEdit && $workflowStep.value === 1) return true;

    return isCreate && $workflowStep.value === 2;
  }

  const isSaveStepEnabled = () => {
    if (isEdit && $workflowStep.value === 2) return true;

    return isCreate && $workflowStep.value === 3;
  }

  const getTitle = () => {
    if (isEdit) return 'Edit Import Templates';

    return 'Import Templates';
  }

  const showErrors = () => $showErrors.value && $errorMessage.value && $errorMessage.value.length > 0

  return (isStartOverReady && isEditDataReady ?

    <Page maxWidth={false} header={
      <Flex center>
        <RowH1 className={classes.title}>{getTitle()}</RowH1>
      </Flex>
    }>
      <Flex row>
        <IngestWorkflowContainer>
          <IngestWorkflow activeStep={$workflowStep.value} steps={isCreate ? workflowStepsForCreate : workflowStepsForEdit} />
        </IngestWorkflowContainer>
      </Flex>
      {
        isDetailsStepEnabled() &&
        <ImportTemplateDetailsStep
          next={goToNextStep}
          cancel={cancel}
          startOver={doStartOver}
          importTemplateDetails={importTemplateDetails}
        />
      }
      {
        isUploadStepEnabled() &&
        <UploadImportTemplateStep
          next={goToNextStep}
          back={goToPreviousStep}
          cancel={cancel}
          startOver={doStartOver}
          importTemplateDetails={importTemplateDetails} />
      }
      {
        isMapperStepEnabled() &&
        <ImportTemplateMapperStep
          handleState={handleState}
          back={goToPreviousStep}
          cancel={cancel}
          startOver={doStartOver}
          importTemplateDetails={importTemplateDetails}
          goToStep={goToStep}
          showSuccessfulEditMessage={$showSuccessfulEditMessage}
        />
      }
      {/* {
        isSaveStepEnabled() &&
        <ImportModelSaveStep
          back={goToPreviousStep}
          cancel={cancel}
          startOver={doStartOver}
          importTemplateDetails={importTemplateDetails}
          goToStep={goToStep}
        />
      } */}

      <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={$showErrors.value} autoHideDuration={6000} onClose={handleCloseShowErrors}>
        <div>
          {showErrors() && $errorMessage.value.map((error) =>
            <StyledAlert severity="error">
              {error}
            </StyledAlert>
          )}</div>
      </Snackbar>

      <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={$showSuccessfulEditMessage.value} autoHideDuration={6000} onClose={handleCloseShowSuccessfulEditMessage}>
        <Alert severity="success">
          Import template successfully updated
        </Alert>
      </Snackbar>

    </Page> : <Loading />
  );
};

const StyledAlert = withStyles({
  root: {
    margin: '8px',
    width: '60vw',
    border: '1px solid lightgray'
  }
})(Alert)

const useStyles = makeStyles({
  title: {
    color: '#3d6889'
  }
});

const IngestWorkflowContainer = div({
  maxWidth: '1000px',
  width: '100%',
  marginLeft: 'auto',
  marginRight: 'auto'
});

const IngestWorkflow = ({ activeStep, steps }: Partial<WorkflowProps>) => CreateWorkflow({
  activeStep: activeStep,
  steps: steps
});

const workflowStepsForCreate = [
  {
    title: 'NAME YOUR TEMPLATES AND FILL OUT DETAILS',
    description: '',
  },
  {
    title: 'UPLOAD SAMPLE FILES',
    description: 'Choose Sample Files to Upload',
  },
  {
    title: 'VIEW PARSE RESULTS, MAP FIELDS AND SAVE IMPORT TEMPLATE',
    description: 'Map results to supported fields',
  }
];

const workflowStepsForEdit = [
  {
    title: 'NAME YOUR TEMPLATES AND FILL OUT DETAILS',
    description: '',
  },
  {
    title: 'VIEW PARSE RESULTS, MAP FIELDS AND SAVE IMPORT TEMPLATE',
    description: 'Map results to supported fields',
  }
];